/**
 * ResolutionDecisionContract (AJIP-16.02.006 · WS4)
 * ---------------------------------------------------------------------
 * Every resolution attempt produces a Resolution Decision. Immutable.
 * Auditable. Deterministic — the same valid input and system state
 * shall produce the same decision (Additional Founder Directive: the
 * Resolution Service is the sole authority for activation eligibility).
 */

const RESOLUTION_DECISIONS = Object.freeze({
  RESOLVED: 'RESOLVED',
  NOT_FOUND: 'NOT_FOUND',
  INELIGIBLE: 'INELIGIBLE',
  FEATURE_DISABLED: 'FEATURE_DISABLED',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  MISSING_PREREQUISITE: 'MISSING_PREREQUISITE',
  VERSION_UNSUPPORTED: 'VERSION_UNSUPPORTED',
  DEPRECATED: 'DEPRECATED',
  CONFLICT: 'CONFLICT',
  RESTORATION_REQUIRED: 'RESTORATION_REQUIRED',
  ACTIVATION_PROHIBITED: 'ACTIVATION_PROHIBITED',
  INVALID_REQUEST: 'INVALID_REQUEST',
});

const CONFLICT_STATUSES = Object.freeze({
  NONE: 'none',
  DUPLICATE_REQUEST: 'DUPLICATE_REQUEST',
  ACTIVE_INSTANCE_EXISTS: 'ACTIVE_INSTANCE_EXISTS',
  INCOMPATIBLE_JOURNEY_ACTIVE: 'INCOMPATIBLE_JOURNEY_ACTIVE',
  BLOCKED_JOURNEY_EXISTS: 'BLOCKED_JOURNEY_EXISTS',
  REENTRY_RESTRICTED: 'REENTRY_RESTRICTED',
  OWNERSHIP_MISMATCH: 'OWNERSHIP_MISMATCH',
  VERSION_CONFLICT: 'VERSION_CONFLICT',
  SIMULTANEOUS_ACTIVATION: 'SIMULTANEOUS_ACTIVATION',
});

let _seq = 0;
const _now = () => Date.now();
const _mintDecisionId = (nowFn) => {
  _seq = (_seq + 1) & 0xffff;
  return `res_${(nowFn ? nowFn() : _now()).toString(36)}_${_seq.toString(36)}`;
};

const makeResolutionDecision = ({
  resolutionDecisionId,
  requestId,
  journeyId,
  journeyVersion,
  decision,
  reason,
  eligibilityDecisionRef = null,
  selectedDefinitionRef = null,
  activationPermitted,
  restorationPermitted,
  conflictStatus = CONFLICT_STATUSES.NONE,
  appliedRules = [],
  evaluatedAt,
  auditMetadata = {},
  nowFn = _now,
}) => {
  if (typeof decision !== 'string' || !Object.values(RESOLUTION_DECISIONS).includes(decision)) {
    throw new Error(`makeResolutionDecision: unknown decision '${decision}'`);
  }
  const id = resolutionDecisionId || _mintDecisionId(nowFn);
  const at = evaluatedAt || new Date(nowFn()).toISOString();
  return Object.freeze({
    resolutionDecisionId: id,
    requestId: requestId || null,
    journeyId: journeyId || null,
    journeyVersion: Number.isInteger(journeyVersion) ? journeyVersion : null,
    decision,
    reason: reason || 'OK',
    eligibilityDecisionRef,
    selectedDefinitionRef,
    activationPermitted: activationPermitted === true,
    restorationPermitted: restorationPermitted === true,
    conflictStatus,
    appliedRules: Object.freeze(Array.isArray(appliedRules) ? [...appliedRules] : []),
    evaluatedAt: at,
    auditMetadata: Object.freeze({ ...auditMetadata }),
  });
};

const isResolvedForActivation = (decision) => {
  if (!decision) return false;
  return decision.decision === RESOLUTION_DECISIONS.RESOLVED && decision.activationPermitted === true;
};

const isResolvedForRestoration = (decision) => {
  if (!decision) return false;
  return decision.decision === RESOLUTION_DECISIONS.RESTORATION_REQUIRED && decision.restorationPermitted === true;
};

export {
  RESOLUTION_DECISIONS,
  CONFLICT_STATUSES,
  makeResolutionDecision,
  isResolvedForActivation,
  isResolvedForRestoration,
};
